import React, { useMemo } from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import css from './SearchResultsPanel.module.css';

const countActiveApplications = applyTransactions => {
  if (!Array.isArray(applyTransactions)) {
    return 0;
  }

  return applyTransactions.filter(
    transaction => transaction?.status !== 'rejected' && transaction?.status !== 'scheduled'
  ).length;
};

const getTransactionTimestamp = transaction => {
  const timestamp = transaction?.updatedAt || transaction?.shortlistedAt || transaction?.scheduledAt || transaction?.rejectedAt || transaction?.createdAt;
  const parsed = new Date(timestamp || 0).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    isMapVariant = true,
    listingTypeParam,
    currentUser,
    currentUserAdminId,
    onInitiateApplyTransaction,
    acceptedConsultantsExist,
    onSearchAcceptedConsultants,
    profileListing,
    allApplyTransactions,
    subscriptionStatus,
    onPayForRFP,
    subscriptionPrices,
    currentUserPersonaRecords,
    onApplyAttemptWithoutAuth,
    onUploadBlobFile,
    onUploadResumeBlobFile,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage';

  const numberOfActiveApplications = useMemo(
    () => countActiveApplications(allApplyTransactions),
    [allApplyTransactions]
  );

  const listingTransactionsById = useMemo(() => {
    const transactionsByListing = new Map();

    if (!Array.isArray(allApplyTransactions)) {
      return transactionsByListing;
    }

    allApplyTransactions.forEach(transaction => {
      const listingId = transaction?.listingId;
      if (!listingId) {
        return;
      }

      const currentTransaction = transactionsByListing.get(listingId);
      if (!currentTransaction) {
        transactionsByListing.set(listingId, transaction);
        return;
      }

      if (getTransactionTimestamp(transaction) >= getTransactionTimestamp(currentTransaction)) {
        transactionsByListing.set(listingId, transaction);
      }
    });

    return transactionsByListing;
  }, [allApplyTransactions]);

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={{ listingType: listingTypeParam }}
        pageSearchParams={search}
        pagination={pagination}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  const listingCardRenderSizes = cardRenderSizes(isMapVariant);

  return (
    <div className={classes}>
      <div className={isMapVariant ? css.listingCardsMapVariant : css.listingCards}>
        {listings.map(l => {
          const listingApplyTransaction = listingTransactionsById.get(l?.id?.uuid) || null;

          return (
            <ListingCard
              className={css.listingCard}
              key={l.id.uuid}
              listing={l}
              renderSizes={listingCardRenderSizes}
              currentUser={currentUser}
              currentUserAdminId={currentUserAdminId}
              onInitiateApplyTransaction={onInitiateApplyTransaction}
              acceptedConsultantsExist={acceptedConsultantsExist}
              onSearchAcceptedConsultants={onSearchAcceptedConsultants}
              profileListing={profileListing}
              listingApplyTransaction={listingApplyTransaction}
              numberOfActiveApplications={numberOfActiveApplications}
              subscriptionStatus={subscriptionStatus}
              onPayForRFP={onPayForRFP}
              subscriptionPrices={subscriptionPrices}
              currentUserPersonaRecords={currentUserPersonaRecords}
              onApplyAttemptWithoutAuth={onApplyAttemptWithoutAuth} // ← NEW
              onUploadBlobFile={onUploadBlobFile}
              onUploadResumeBlobFile={onUploadResumeBlobFile}
            />
          );
        })}
        {props.children}
      </div>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
